import { useEffect, useRef } from 'react'
import Icon from '../lib/icon.jsx'
import { tap } from '../lib/feedback.js'
import { useAvatar, getAvatarColor } from '../lib/avatar-store.js'

const TAB_WIDTH = 64
const TAB_HEIGHT = 56

export function BottomNav({ tabs = [], activeTab, onTabChange }) {
  const indicatorRef = useRef(null)
  const { emoji, colorId } = useAvatar()
  const avatar = getAvatarColor(colorId)

  // Slide the indicator to whichever tab is current and recolor it to
  // that tab's hue. Position glides; color fades a beat faster so the
  // new color settles before the slide lands.
  useEffect(() => {
    const el = indicatorRef.current
    if (!el) return
    const idx = tabs.findIndex((t) => t.id === activeTab)
    if (idx < 0) {
      el.style.opacity = '0'
      return
    }
    const tab = tabs[idx]
    el.style.transform = `translateX(${idx * TAB_WIDTH}px)`
    el.style.opacity = '1'
    el.style.background = tab.color
    el.style.borderBottomColor = tab.edge
  }, [activeTab, tabs])

  return (
    <>
      {/* Progressive blur backdrop — fades toward the top so the nav
          floats over the feed content without a hard edge. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 h-32 pointer-events-none"
        style={{
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          maskImage: 'linear-gradient(to top, black 30%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to top, black 30%, transparent 100%)',
        }}
        aria-hidden="true"
      />

    <nav
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white"
      style={{
        border: '2px solid #EFE9E0',
        borderRadius: 999,
        boxShadow: '0 4px 0 rgba(26, 26, 26, 0.16)',
      }}
      aria-label="Navigation"
    >
      <div className="relative flex items-center p-1.5">
        {/* Sliding active indicator — bg + bottom edge recolor per tab */}
        <div
          ref={indicatorRef}
          className="absolute top-1.5 left-1.5 pointer-events-none"
          style={{
            width: TAB_WIDTH,
            height: TAB_HEIGHT,
            borderRadius: 999,
            background: 'transparent',
            borderBottom: '4px solid transparent',
            opacity: 0,
            transition:
              'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s ease-out, border-bottom-color 0.2s ease-out, opacity 0.2s ease-out',
            willChange: 'transform',
          }}
          aria-hidden="true"
        />

        {tabs.map((tab) => {
          const isActive = activeTab === tab.id
          const isProfile = tab.id === 'profile'
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => { tap(); onTabChange?.(tab.id) }}
              className="relative z-10 flex items-center justify-center cursor-pointer transition-transform duration-200 ease-out active:scale-[0.92]"
              style={{ width: TAB_WIDTH, height: TAB_HEIGHT }}
              aria-label={tab.label}
              aria-current={isActive ? 'page' : undefined}
            >
              {isProfile ? (
                /* Profile tab renders the user's avatar as the visual.
                   pointer-events: none so taps go to the parent button.
                   When active, lift 2px to optically center on the
                   indicator pill's face (the 4px bottom shelf shifts
                   the visual center up by 2px). */
                <div
                  className="flex items-center justify-center rounded-full pointer-events-none transition-transform duration-200"
                  style={{
                    width: 38,
                    height: 38,
                    background: avatar.face,
                    border: `2px solid ${avatar.outline}`,
                    fontSize: 22,
                    lineHeight: 1,
                    transform: isActive ? 'translateY(-2px)' : 'none',
                  }}
                  aria-hidden="true"
                >
                  <span>{emoji}</span>
                </div>
              ) : (
                <Icon
                  name={tab.icon}
                  size={24}
                  aria-hidden="true"
                  className="transition-colors duration-200"
                  style={{
                    color: isActive ? '#ffffff' : tab.color,
                    // Lift active icon 2px to land on the optical center
                    // of the indicator's face (the 4px bottom shelf
                    // pulls the geometric center down).
                    transform: isActive ? 'translateY(-2px)' : 'none',
                    transition: 'color 200ms, transform 200ms',
                  }}
                />
              )}
            </button>
          )
        })}
      </div>
    </nav>
    </>
  )
}
